import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { IoCodeSlashOutline, IoCreateOutline } from "react-icons/io5";
import { useNavigate } from "react-router";

import ReadParameter from "./ReadParameter.graphql";
import UpsertParameter from "./UpsertParameter.graphql";
import useNotifications, { notifyMutationErrors } from "../../../../../components/Notifications";
import * as UI from "../../../../../modules/ui-kit";
import CodeEditor from "../../../components/CodeEditor";
import { StyledEditorLayout } from "../../../components/StyledEditorLayout";
import { sameSource } from "../../../modules/compare";
import { useUnsavedChangesEditor } from "../../../modules/unsaved-changes";
import { ACCESS_TIER_OPTIONS, DEFAULT_ACCESS_TIER, effectiveTier } from "../../accessTiers";

export default function ParameterEditor(props) {
    const { guard, id } = props;
    const navigate = useNavigate();

    const notifications = useNotifications();

    const [mode, setMode] = useState<"form" | "json">("form");

    const readParameter = useQuery(
        gql(ReadParameter),
        {
            variables: {
                id,
            },
        },
    );

    const storedSource = readParameter.data?.parameter?.sourceDefinition ?? {};

    const [currentSource, setCurrentSource] = useState(undefined);
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState(undefined);
    const [saving, setSaving] = useState(false);
    // tier lives outside sourceDefinition - it is a mutation argument
    const [tier, setTier] = useState(undefined);



    useEffect(
        () => {
            setCurrentSource(undefined);
            setJsonText(JSON.stringify(storedSource, null, 2));
            setJsonError(undefined);
            setTier(undefined);
        },
        [id, readParameter.data],
    );

    const activeSource = currentSource ?? storedSource;

    const storedTier = readParameter.data?.parameter?.ownTier ?? undefined;
    const activeTier = tier ?? storedTier ?? DEFAULT_ACCESS_TIER;
    const tierDirty = tier !== undefined && tier !== storedTier;
    const group = readParameter.data?.parameter?.group;
    const finalTier = effectiveTier(group?.tier, tier ?? storedTier);

    const dirty = !sameSource(activeSource, storedSource) || tierDirty;

    const [upsertParameter] = useMutation(
        gql(UpsertParameter),
        {
            awaitRefetchQueries: true,
            refetchQueries: [
                gql(ReadParameter),
                "ListParameters",
            ],
            variables: {
                id,
            },
        },
    );

    function updateField(key: string, value: unknown) {
        setCurrentSource({
            ...activeSource,
            [key]: value,
        });
    }

    function onJsonChange(value: string | undefined) {
        const text = value ?? "";
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setCurrentSource(parsed);
            setJsonError(undefined);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }

    function switchMode(targetMode: "form" | "json") {
        setJsonError(undefined);
        if (targetMode === "json") {
            setJsonText(JSON.stringify(activeSource, null, 2));
        }
        setMode(targetMode);
    }

    async function save() {
        if (jsonError && mode === "json") return false;

        setSaving(true);

        try {
            const result = await upsertParameter({
                variables: {
                    definition: activeSource,
                    // omitted unless changed, so the stored tier is preserved
                    ...(tierDirty && { tier: activeTier }),
                },
            });

            if (result.data?.upsertParameter?.errors?.length) {
                notifyMutationErrors(notifications, result.data.upsertParameter.errors);
                return false;
            }

            setCurrentSource(undefined);
            setTier(undefined);
            notifications.success("Saved.");

            const newID = result.data?.upsertParameter?.parameter?.id;
            if (newID && newID !== id) {
                navigate(`../${ newID }`);
            }

            return true;
        } finally {
            setSaving(false);
        }
    }

    useUnsavedChangesEditor(guard, save, dirty);

    if (readParameter.loading) return "loading";
    if (readParameter.error) return "error";

    function onSubmit(event) {
        event.preventDefault();
        void save();
    }

    function onReset() {
        setCurrentSource(undefined);
        setJsonText(JSON.stringify(storedSource, null, 2));
        setJsonError(undefined);
    }


    return (
        <StyledEditorLayout>
            <div className="mode-tabs">
                <button
                    className={ mode === "form" ? "active" : "" }
                    onClick={ () => switchMode("form") }
                    type="button"
                >
                    <IoCreateOutline /> Form View
                </button>
                <button
                    className={ mode === "json" ? "active" : "" }
                    onClick={ () => switchMode("json") }
                    type="button"
                >
                    <IoCodeSlashOutline /> JSON Source
                </button>
            </div>

            <UI.Form
                className="editor-body"
                onReset={ onReset }
                onSubmit={ onSubmit }
            >
                { mode === "form" ? (
                    <>
                        <UI.Field>
                            <UI.FieldLabel>name</UI.FieldLabel>
                            <UI.FieldDescription>Multi-language translations for the parameter name</UI.FieldDescription>
                            <UI.TranslatedInput
                                disabled={ saving }
                                label="name"
                                onChange={ (val) => updateField("name", val) }
                                value={ activeSource.name ?? [] }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>dataType</UI.FieldLabel>
                            <UI.FieldDescription>Parameter primitive data type (text, number, boolean)</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ (val) => updateField("dataType", val) }
                                placeholder="e.g. number / text / boolean"
                                value={ activeSource.dataType ?? activeSource.type ?? "" }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>parameterType</UI.FieldLabel>
                            <UI.FieldDescription>Domain parameter type classification</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ (val) => updateField("parameterType", val) }
                                placeholder="e.g. Length / Angle / Text"
                                value={ activeSource.parameterType ?? "" }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>units</UI.FieldLabel>
                            <UI.FieldDescription>Measurement unit symbol (e.g. mm, kg, m²)</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ (val) => {
                                    updateField("unit", val);
                                    updateField("units", val ? [val] : []);
                                } }
                                placeholder="e.g. mm"
                                value={ activeSource.unit ?? (activeSource.units?.[0] ?? "") }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>deprecated</UI.FieldLabel>
                            <UI.FieldDescription>Toggle whether this parameter is deprecated</UI.FieldDescription>
                            <UI.SegmentedControl
                                disabled={ saving }
                                onChange={ (val) => updateField("deprecated", val) }
                                options={ [
                                    { label: "Deprecated", value: true },
                                    { label: "Not deprecated", value: false },
                                ] }
                                value={ Boolean(activeSource.deprecated) }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>hidden</UI.FieldLabel>
                            <UI.FieldDescription>Toggle whether this parameter is hidden</UI.FieldDescription>
                            <UI.SegmentedControl
                                disabled={ saving }
                                onChange={ (val) => updateField("hidden", val) }
                                options={ [
                                    { label: "Hidden", value: true },
                                    { label: "Visible", value: false },
                                ] }
                                value={ Boolean(activeSource.hidden) }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>own tier</UI.FieldLabel>
                            <UI.FieldDescription>
                                Set on this parameter. The final tier is the stricter of this and the group&apos;s tier.
                                { tier === undefined && storedTier === undefined && " Not set yet." }
                            </UI.FieldDescription>
                            <UI.Select
                                isDisabled={ saving }
                                onSelect={ setTier }
                                options={ ACCESS_TIER_OPTIONS }
                                value={ activeTier }
                            />
                            <UI.FieldDescription>
                                group tier: { group ? `${ group.tier ?? "not set" } (${ group.name?.[0]?.text ?? group.id })` : "no group" }
                            </UI.FieldDescription>
                            <UI.FieldDescription>
                                final tier: <strong>{ finalTier }</strong>{ tierDirty && " (after saving)" }
                            </UI.FieldDescription>
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>about</UI.FieldLabel>
                            <UI.FieldDescription>Multi-language description explaining this parameter</UI.FieldDescription>
                            <UI.TranslatedInput
                                disabled={ saving }
                                label="about"
                                onChange={ (val) => {
                                    updateField("about", val);
                                    updateField("description", val);
                                } }
                                value={ activeSource.about ?? activeSource.description ?? [] }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>note</UI.FieldLabel>
                            <UI.FieldDescription>Internal notes or documentation</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ (val) => updateField("note", val) }
                                placeholder="Internal note..."
                                value={ activeSource.note ?? "" }
                            />
                        </UI.Field>
                    </>
                ) : (
                    <UI.Field className="json-field">
                        <UI.FieldLabel>Raw JSON Definition</UI.FieldLabel>
                        <UI.FieldDescription>Edit raw parameter JSON definition (name, type, unit, group, deprecated, description, values)</UI.FieldDescription>
                        <div className="json-editor-wrapper">
                            <CodeEditor
                                key={ `${ id }-${ mode }` }
                                onChange={ onJsonChange }
                                value={ jsonText }
                            />
                        </div>
                        { jsonError && <span className="errors">Invalid JSON: { jsonError }</span> }
                    </UI.Field>
                ) }


                <UI.ButtonGroup>
                    <UI.Button
                        disabled={ !dirty || saving }
                        type="reset"
                    >
                        Reset
                    </UI.Button>
                    <UI.Button
                        $appearance="primary"
                        disabled={ !dirty || (mode === "json" && !!jsonError) || saving }
                        type="submit"
                    >
                        { saving ? "Saving…" : "Save" }
                    </UI.Button>
                </UI.ButtonGroup>
            </UI.Form>
        </StyledEditorLayout>
    );
}
